import React, { useEffect, useMemo, useState } from "react";
import cogoToast from "cogo-toast";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import Select from "react-select";
import { ENDPOINTS, SUCCESS_RESULT } from "../Constants";
import { genericGet } from "../services/Api";

const FIELD_ORDER = ["country", "zone", "region", "district", "village", "camp"];

const extractQuestions = (templateData) => {
  const pages = Array.isArray(templateData?.pages) ? templateData.pages : [];
  const allElements = [];

  pages.forEach((page) => {
    const pageElements = Array.isArray(page?.elements) ? page.elements : [];
    pageElements.forEach((element) => {
      if (element?.type === "panel" && Array.isArray(element?.elements)) {
        allElements.push(...element.elements);
      } else {
        allElements.push(element);
      }
    });
  });

  const byName = {};
  allElements.forEach((element) => {
    if (element?.type === "dropdown" && element?.name) {
      byName[element.name] = element;
    }
  });

  return byName;
};

const parseVisibleIf = (visibleIf) => {
  if (!visibleIf || typeof visibleIf !== "string") return null;
  const match = visibleIf.match(/\{([^}]+)\}\s*==\s*'([^']+)'/);
  if (!match) return null;
  return {
    dependsOn: match[1].trim(),
    expectedValue: match[2].trim(),
  };
};

const isChoiceVisible = (choice, selectedValues) => {
  const rule = parseVisibleIf(choice?.visibleIf);
  if (!rule) return true;
  return String(selectedValues?.[rule.dependsOn] ?? "") === String(rule.expectedValue);
};

const normalizeChoice = (choice) => ({
  value: String(choice?.value ?? ""),
  text: String(choice?.text ?? choice?.value ?? ""),
});

const getVisibleChoices = (question, selectedValues) => {
  const choices = Array.isArray(question?.choices) ? question.choices : [];
  return choices
    .filter((choice) => isChoiceVisible(choice, selectedValues))
    .map(normalizeChoice);
};

const LocationPickerModal = ({
  isOpen,
  toggle,
  onSelect,
  title = "Select Location",
  initialValue = null,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [templateQuestions, setTemplateQuestions] = useState({});
  const [selectedValues, setSelectedValues] = useState({
    country: "",
    zone: "",
    region: "",
    district: "",
    village: "",
    camp: "",
  });

  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    genericGet(ENDPOINTS.LOCATION_TEMPLATE)
      .then((res) => {
        const status = res?.data?.status;
        if (res?.status === 200 && status === SUCCESS_RESULT) {
          setTemplateQuestions(extractQuestions(res?.data?.data));
          return;
        }
        cogoToast.error(res?.data?.message || "Failed to load location template", { hideAfter: 4 });
      })
      .catch(() => {
        cogoToast.error("Failed to load location template", { hideAfter: 4 });
      })
      .finally(() => setIsLoading(false));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedValues({
      country: String(initialValue?.country ?? ""),
      zone: String(initialValue?.zone ?? ""),
      region: String(initialValue?.region ?? ""),
      district: String(initialValue?.district ?? ""),
      village: String(initialValue?.village ?? ""),
      camp: String(initialValue?.camp ?? ""),
    });
  }, [initialValue, isOpen]);

  const optionsByField = useMemo(() => {
    const result = {};
    FIELD_ORDER.forEach((name) => {
      result[name] = getVisibleChoices(templateQuestions?.[name], selectedValues);
    });
    return result;
  }, [templateQuestions, selectedValues]);

  const selectOptionsByField = useMemo(() => {
    const result = {};
    FIELD_ORDER.forEach((name) => {
      result[name] = (optionsByField[name] || []).map((choice) => ({
        value: choice.value,
        label: choice.text,
      }));
    });
    return result;
  }, [optionsByField]);

  const getChoiceText = (fieldName, value) => {
    const match = (optionsByField?.[fieldName] || []).find((choice) => choice.value === value);
    return match?.text || value;
  };

  const handleChange = (fieldName, value) => {
    const index = FIELD_ORDER.indexOf(fieldName);
    setSelectedValues((prev) => {
      const next = { ...prev, [fieldName]: value };
      for (let i = index + 1; i < FIELD_ORDER.length; i += 1) {
        next[FIELD_ORDER[i]] = "";
      }
      return next;
    });
  };

  const canSubmit = String(selectedValues?.country || "").trim().length > 0;

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.15rem rgba(13,110,253,.2)" : "none",
      "&:hover": { borderColor: "#86b7fe" },
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  };

  useEffect(() => {
    if (!isOpen || isLoading) return;
    const countryOptions = selectOptionsByField.country || [];
    if (!countryOptions.length) return;
    if (selectedValues.country) return;
    handleChange("country", countryOptions[0].value);
  }, [isOpen, isLoading, selectOptionsByField.country, selectedValues.country]);

  const handleSelect = () => {
    if (!canSubmit) {
      cogoToast.error("Please select at least country", {
        hideAfter: 3,
      });
      return;
    }

    const payload = {
      country: selectedValues.country,
      zone: selectedValues.zone,
      district: selectedValues.district,
      village: selectedValues.village,
      camp: selectedValues.camp,
      labels: {
        country: getChoiceText("country", selectedValues.country),
        zone: getChoiceText("zone", selectedValues.zone),
        district: getChoiceText("district", selectedValues.district),
        village: getChoiceText("village", selectedValues.village),
        camp: getChoiceText("camp", selectedValues.camp),
      },
    };

    if (typeof onSelect === "function") {
      onSelect(payload);
    }
    if (typeof toggle === "function") {
      toggle();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <ModalBody>
        {isLoading ? (
          <div className="text-muted">Loading location template...</div>
        ) : (
          <div className="row">
            {[
              ["country", "Country"],
              ["zone", "Zone"],
              ["region", "Region"],
              ["district", "District"],
              ["village", "Village"],
              ["camp", "Camp"],
            ].map(([fieldName, label]) => (
              <div className="col-md-6 mb-3" key={fieldName}>
                <label className="font-weight-bold mb-1">{label}</label>
                <Select
                  styles={selectStyles}
                  isSearchable
                  isClearable
                  options={selectOptionsByField[fieldName] || []}
                  value={
                    (selectOptionsByField[fieldName] || []).find(
                      (opt) => opt.value === selectedValues[fieldName]
                    ) || null
                  }
                  onChange={(option) => handleChange(fieldName, option?.value || "")}
                  isDisabled={!selectOptionsByField[fieldName]?.length}
                  placeholder={`Select ${label.toLowerCase()}`}
                  noOptionsMessage={() => "No options"}
                />
              </div>
            ))}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-outline-secondary" onClick={toggle}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={handleSelect} disabled={isLoading || !canSubmit}>
          Select
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default LocationPickerModal;
